import React, { useMemo } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { formatDate } from '../../Utils';

const TIME_RANGES = ['morning', 'midday', 'afternoon'];
const COLLAPSED_DAY_KEY = '__collapsed__';

const getSlotMoment = (slot) => {
    if (slot?.availableDateTime) {
        const fromDateTime = moment(slot.availableDateTime);
        if (fromDateTime.isValid()) {
            return fromDateTime;
        }
    }

    if (slot?.availableDate && slot?.availableTime) {
        const fromDateAndTime = moment(`${slot.availableDate} ${slot.availableTime}`, [
            'YYYY-MM-DD HH:mm:ss',
            'YYYY-MM-DD HH:mm',
            'MM-DD-YYYY HH:mm:ss',
            'MM-DD-YYYY HH:mm',
        ]);
        if (fromDateAndTime.isValid()) {
            return fromDateAndTime;
        }
    }

    if (slot?.dateObj && slot?.time24) {
        const fromDeliverySlot = moment(`${formatDate(slot.dateObj)} ${slot.time24}`, [
            'MM-DD-YYYY HH:mm',
            'MM-DD-YYYY HH:mm:ss',
            'YYYY-MM-DD HH:mm',
            'YYYY-MM-DD HH:mm:ss',
        ]);
        if (fromDeliverySlot.isValid()) {
            return fromDeliverySlot;
        }
    }

    return null;
};

const getTimeRange = (slotMoment) => {
    const hour = slotMoment.hour();
    if (hour < 11) {
        return 'morning';
    }
    if (hour < 14) {
        return 'midday';
    }
    return 'afternoon';
};

const AppointmentResults = ({
    appointmentSlots,
    activeDayKey,
    setActiveDayKey,
    activeTimeRange,
    setActiveTimeRange,
    selectedSlotIndex,
    onSlotSelect,
    onConfirmSelection,
    isBooking,
    confirmLabel,
    bookingLabel,
}) => {
    const { t } = useTranslation();

    const TIME_RANGE_META = {
        morning: {
            title: t('appointment.timeRanges.morning.title'),
            hours: t('appointment.timeRanges.morning.hours'),
        },
        midday: {
            title: t('appointment.timeRanges.midday.title'),
            hours: t('appointment.timeRanges.midday.hours'),
        },
        afternoon: {
            title: t('appointment.timeRanges.afternoon.title'),
            hours: t('appointment.timeRanges.afternoon.hours'),
        },
    };

    const { groupedByDay, sortedDayKeys } = useMemo(() => {
        const grouped = {};

        (appointmentSlots || []).forEach((slot, idx) => {
            const slotMoment = getSlotMoment(slot);
            if (!slotMoment || !slotMoment.isValid()) {
                return;
            }

            const dayKey = slotMoment.format('YYYY-MM-DD');
            if (!grouped[dayKey]) {
                grouped[dayKey] = {
                    slotMoment,
                    slots: [],
                    ranges: {
                        morning: [],
                        midday: [],
                        afternoon: [],
                    },
                };
            }

            const range = getTimeRange(slotMoment);
            grouped[dayKey].slots.push({ idx, slotMoment, range });
            grouped[dayKey].ranges[range].push({ idx, slotMoment, range });
        });

        return {
            groupedByDay: grouped,
            sortedDayKeys: Object.keys(grouped).sort(),
        };
    }, [appointmentSlots]);

    if (!appointmentSlots?.length || !sortedDayKeys.length) {
        return null;
    }

    const selectedDayKey = activeDayKey === COLLAPSED_DAY_KEY
        ? null
        : activeDayKey && groupedByDay[activeDayKey]
            ? activeDayKey
            : sortedDayKeys[0];

    return (
        <Form.Group controlId="selectTimeSlot" className="mb-4">
            <p className="text-muted mb-3">{t('appointment.selectDayPrompt')}</p>
            <div className="d-flex flex-column gap-3">
                {sortedDayKeys.map((dayKey) => {
                    const dayGroup = groupedByDay[dayKey];
                    const isActiveDay = selectedDayKey === dayKey;

                    return (
                        <div key={dayKey}>
                            <button
                                type="button"
                                className={`btn w-100 border rounded-3 text-start p-3 ${isActiveDay ? 'border-primary bg-primary-opacity-10' : 'bg-white border-light'}`}
                                onClick={(event) => {
                                    if (isActiveDay) {
                                        setActiveDayKey(COLLAPSED_DAY_KEY);
                                        event.currentTarget.blur();
                                        return;
                                    }

                                    setActiveDayKey(dayKey);
                                }}
                            >
                                <div className="d-flex justify-content-between align-items-center text-left">
                                    <div>
                                        <div className="fw-semibold fs-5 text-dark">
                                            {dayGroup.slotMoment.format('ddd MMM D')}
                                        </div>
                                        <div className="text-muted">
                                            {dayGroup.slotMoment.format('dddd, MMMM D, YYYY')}
                                        </div>
                                    </div>
                                    <div className="text-end fw-semibold text-primary">
                                        {dayGroup.slots.length}{' '}
                                        {dayGroup.slots.length === 1
                                            ? t('appointment.slotAvailableSingular')
                                            : t('appointment.slotAvailablePlural')}
                                    </div>
                                </div>
                            </button>

                            {isActiveDay && (
                                <div className="appointment-time-range-wrapper px-3 pb-3">
                                    <div className="mt-3 mb-2 text-muted">{t('appointment.chooseTimeRange')}</div>
                                    <div className="border-start border-4 border-primary ps-3">
                                        {TIME_RANGES.filter((range) => dayGroup.ranges[range].length > 0).map((range) => {
                                            const rangeSlots = dayGroup.ranges[range];
                                            const rangeCount = rangeSlots.length;
                                            const rangeMeta = TIME_RANGE_META[range];
                                            const availableRanges = TIME_RANGES.filter((key) => dayGroup.ranges[key].length > 0);
                                            const effectiveActiveTimeRange = activeTimeRange === null
                                                ? null
                                                : availableRanges.includes(activeTimeRange)
                                                    ? activeTimeRange
                                                    : availableRanges[0];
                                            const isRangeActive = effectiveActiveTimeRange === range;
                                            const selectedSlotInRange = rangeSlots.some(({ idx }) => idx === selectedSlotIndex);
                                            return (
                                                <div key={range} className="mb-2">
                                                    <button
                                                        type="button"
                                                        className={`time-range-button w-100 text-start btn btn-outline-secondary rounded-3 p-3 ${isRangeActive ? 'bg-primary-opacity-10 border-primary' : 'bg-white'}`}
                                                        onClick={() => setActiveTimeRange(isRangeActive ? null : range)}
                                                    >
                                                        <div className="d-flex justify-content-between align-items-center text-left">
                                                            <div>
                                                                <div className="fw-semibold fs-4 text-dark">{rangeMeta.title}</div>
                                                                <div className="text-muted">{rangeMeta.hours}</div>
                                                            </div>
                                                            <div className="fw-semibold text-primary">
                                                                {rangeCount}{' '}
                                                                {rangeCount === 1
                                                                    ? t('appointment.slotSingular')
                                                                    : t('appointment.slotPlural')}
                                                                <span className="ml-2 text-muted" aria-hidden="true">-&gt;</span>
                                                            </div>
                                                        </div>
                                                    </button>

                                                    {isRangeActive && rangeCount > 0 && (
                                                        <div className="pt-2">
                                                            <div className="text-muted mb-2">{t('appointment.selectPreferredTimePrompt')}</div>
                                                            <Row className="gx-2 gy-3 mb-2">
                                                                {rangeSlots.map(({ idx, slotMoment }) => (
                                                                    <Col xs={12} sm={6} className="mb-3" key={`slot-grid-${idx}`}>
                                                                        <Button
                                                                            variant="outline-secondary"
                                                                            className={`w-100 py-3 fw-semibold ${idx === selectedSlotIndex ? 'bg-primary-opacity-10 border-primary text-dark' : ''}`}
                                                                            onClick={() => onSlotSelect(idx)}
                                                                        >
                                                                            {slotMoment.format('h:mm A')}
                                                                        </Button>
                                                                    </Col>
                                                                ))}
                                                            </Row>

                                                            {selectedSlotInRange && (
                                                                <div className="mb-2">
                                                                    <Button
                                                                        variant="primary"
                                                                        className="w-100 py-2 fw-semibold"
                                                                        onClick={onConfirmSelection}
                                                                        disabled={isBooking}
                                                                    >
                                                                        <span className="d-inline-flex align-items-center">
                                                                            {isBooking ? bookingLabel : confirmLabel}
                                                                            <span className="ml-2" aria-hidden="true">-&gt;</span>
                                                                        </span>
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </Form.Group>
    );
};

export default AppointmentResults;
